import { useMemo } from 'react';
import axios from 'axios';
import { getCategories, type CategoryDef, type CategoryKind } from './catalogCategories';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import { callError } from './callError';
import { haltGate } from './runHalt';
import { setCatalogNamesFr } from './catalogItems';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { loadPdf, renderPdfPageAnnotated, renderPdfPageMasked, renderPdfPageToImage } from './pdfRender';
import { asPicture, withDoc } from './promptPictures';
// The one test for "this box encloses something", shared with the crops that are cut from
// it. partCrop takes only a TYPE from this module, so the edge back is erased at build and
// there is no cycle at runtime.
import { hasBox } from './partCrop';
import type { Bands } from './categoryBands';
import { RUN_MODEL } from './models';
import type { PartBox } from './PdfViewer';
import { useLanguage } from '../../contexts/LanguageContext';

// EVERY PART AT ONCE, as a value `rereading` can take. -1 and not a string so the field stays
// one type to compare against; it cannot collide with a part index, which counts from 0.
//
// It means "the list on screen is the PREVIOUS run's and the call that replaces it is out" —
// the whole-list twin of the single index the one-part re-read sets. See extractParts.
export const ALL_REREAD = -1;

// One box call's answer. `page` for the many-parts form, which asks about a page; `part` for
// the one-part form, which asks about a part and is told which page it found it on.
export type RedoNote = {
  page: number | null;
  part: number | null;
  reasoning: string;
  raw: string;
};

// The supplied-parts extraction for a document. Like categoryRun, it lives at
// module scope (keyed by document id) so it survives leaving and returning to
// the reader tab, and is persisted server-side (docState). One latest result per doc
// (no history): re-running replaces it.

// One extracted part. `bbox` is PAGE-relative (x0,y0,x1,y1 in 0..1 from the top
// left of the page), so the table can crop it straight from the rendered page.
// Everything known about how big a part is, as one attribute — because "how big is this"
// is one question, and the assembly phase, the video prompt and the table all want the
// answer rather than a choice between two fields.
//
// Filled by two different calls, and a part almost never has both. `spec` is what the
// manual PRINTS ("M6 x 70 mm"), transcribed by the Phase 2 parts read, which has the page
// in front of it. `x/y/z` are millimetres read off the retailer's dimension drawing by the
// dimension call, which is the only one that can measure. Manuals give thread specs for
// bolts and nothing for a seat; retailers' drawings dimension the seat and ignore the
// bolts.
//
// A THREADED FASTENER is sized by neither of those. Three axes do not describe a screw:
// what identifies one is its thread and its length, and a 6 x 6 x 70 box would be a true
// statement no supplier catalogue can be searched with. So the parts read breaks the
// printed designation down: `thread` ("M6") and `lengthMm` (70, the OVERALL length of
// the screw, a supplier table's "Lg." rather than its "Thread Lg."). `spec` keeps the raw
// quote either way, and is what to check the breakdown against.
//
// A WASHER PRINTED "M6" HAS NO THREAD, and that is `forScrew`: the screw it goes around,
// a supplier's "For Screw Size". Not a pedantic distinction — it describes the hole and
// says nothing about the outside, where an M6 washer is 11.8 mm across in DIN 127B, 12 in
// DIN 125 and 18 in DIN 9021.
//
// A BARREL NUT PRINTED "M6" is `forScrew` too, and it is where the rule had to be stated
// properly: it IS threaded, but female, cut to take an M6. So the two fields do not split
// on "threaded or not" — they split on WHOSE thread the designation names. Its own
// outside is `thread`; the fastener it receives or surrounds is `forScrew`.
//
// Which leaves a cylinder's own two extents, neither of them in the designation:
// `lengthMm` (a supplier's "Lg.", already there for screws) and `bodyDiaMm` ("Body
// Dia."). An M6 barrel nut is ~10 mm across because that is the market, not because it is
// M6, and its length is set by the panel it crosses.
//
// A SCREW HAS THE SAME TWO HALVES, and its second extent is the HEAD. `headDiaMm` is a
// supplier's "Dia." column — the head is what stops the screw and the only part of it left
// above the panel, and 8 mm for a 4 mm chipboard screw is the market again rather than
// anything "M4" says. Its head HEIGHT is deliberately not a field: it follows from the
// diameter and the countersink angle to within a few percent (McMaster's No. 4 computes to
// 0.065" against a printed 0.067"), and two stored numbers that should agree eventually
// will not.
//
// A FASTENER COVER IS A THIRD CYLINDER, and the one that needs a number the others do
// not: `fitsHoleMm`, the bore its plug drops into. A wood button, a hole plug, a cover cap
// — you buy them BY that hole, because the head you see is the part that varies. A 14.5 mm
// head caps an 8 mm bore or a 10 mm one depending on who made it, so the two are
// independent and neither follows from the other. It is a MEASUREMENT and not a
// designation, unlike `forScrew`, which is why it is not that field: a washer's "M6" names
// a thread, and a cover's 8 mm names a drilled hole.
//
// `drive` is the recess that turns it — "PZ2", "T20", "slotted". The only one of these
// that is not a measurement, and it is here because it is the feature the whole mesh
// repair path exists to cut: a screw whose drive is a field is a screw whose recess is a
// parameter rather than a hand-driven repair. Being categorical it is also the one field
// no estimate is ever asked for; see measurableKeys.
//
// Millimetres and integers throughout: the drawings are in centimetres and the manual in
// millimetres, and one unit everywhere beats a unit field nobody reads. Any axis can be
// null on its own — a leg's length is dimensioned and its cross-section is not, and half
// an answer is worth keeping. `source` rides inside so provenance travels with the number.
export type PartSize = {
  x: number | null;
  y: number | null;
  z: number | null;
  // THE TWO ENDS OF THE WIDTH AND OF THE DEPTH, bottom then top, where the drawing printed
  // both — a pair or nothing (see _drawn_axes). `x` and `y` stay the box, the largest figure
  // on the axis; these are the shape inside it. Optional: every size stored before them.
  xBottom?: number | null;
  xTop?: number | null;
  yBottom?: number | null;
  yTop?: number | null;
  spec: string | null;
  thread: string | null;
  lengthMm: number | null;
  bodyDiaMm: number | null;
  headDiaMm: number | null;
  fitsHoleMm: number | null;
  // A HEX KEY IS AN L, AND McMASTER SIZES IT IN THREE. `driveSizeMm` is its "Drive Size,
  // mm" — the across-flats of the hex, and the only number that decides whether the key
  // turns the screw. `lengthMm` is its long leg, which is also its overall length: their
  // table prints both columns and they are equal on every row, an L having no other end.
  // `shortLegMm` is the other one.
  //
  // No `forScrew`, and that is McMaster's own answer to a question this app had asked
  // twice: their table has no such column, because a key is identified by the hex it
  // turns and not by the thread behind it. The printed "M4 x 100mm" on a flat-pack key
  // cannot be resolved anyway — it is the driven screw on one reading and the key's own
  // 4 mm on another, and those give different hexes — so it stays in `spec`, quoted and
  // unresolved, which is what `spec` is for.
  driveSizeMm: number | null;
  shortLegMm: number | null;
  drive: string | null;
  forScrew: string | null;
  // A SCREW'S POINT (Louis 2026-10-29): 'pointed' cuts its own way through every part it crosses,
  // so none is bored for it (see makesOwnHole); 'blunt' goes into a hole, a nut or an insert. Read
  // off the drawing by the parts call, or set by hand (see ScrewTip). Optional: sizes stored before.
  tip?: 'pointed' | 'blunt' | null;
  source: 'measured' | 'derived' | null;
  note: string | null;
};

// The half of a size that the MANUAL answered, and the half a DRAWING did. Two calls fill
// this object and each must leave the other's fields alone, so both merge sites split it
// the same way rather than listing the keys they happen to remember — which is how `spec`
// used to survive a dimension read while anything added beside it silently would not.
export const printedSize = (size: PartSize | null | undefined) => ({
  spec: size?.spec ?? null,
  thread: size?.thread ?? null,
  lengthMm: size?.lengthMm ?? null,
  bodyDiaMm: size?.bodyDiaMm ?? null,
  headDiaMm: size?.headDiaMm ?? null,
  fitsHoleMm: size?.fitsHoleMm ?? null,
  drive: size?.drive ?? null,
  forScrew: size?.forScrew ?? null,
});

// And the half a DRAWING did: the box, and the ends inside it. A re-read keeps all of it.
const drawnSize = (size: PartSize) => ({
  x: size.x, y: size.y, z: size.z,
  xBottom: size.xBottom ?? null, xTop: size.xTop ?? null,
  yBottom: size.yBottom ?? null, yTop: size.yTop ?? null,
});

// Whether a size says anything at all. An object of nulls is stored as no size, because
// `size ? … : …` is the test a dozen callers make and "read and empty" would pass it.
export const hasSize = (size: PartSize | null | undefined) =>
  !!size && (size.x != null || size.y != null || size.z != null
    || size.xBottom != null || size.yBottom != null
    || !!size.spec || !!size.thread || size.lengthMm != null || size.bodyDiaMm != null
    || size.headDiaMm != null || size.fitsHoleMm != null
    || size.driveSizeMm != null || size.shortLegMm != null
    || !!size.drive || !!size.forScrew || !!size.tip);

// A stored part from before the merge held a bare string here, which was the spec.
export function coerceSize(value: PartSize | string | null | undefined): PartSize | null {
  if (typeof value === 'string') {
    const spec = value.trim();
    return spec ? { ...NO_SIZE, spec } : null;
  }
  return value ?? null;
}

// WHETHER THE SEVERAL UNITS OF A PART ARE ONE OBJECT REPEATED OR A MIRROR PAIR.
//
// The question only a supplied quantity can raise, and the one thing a parts list has never
// said: two side rails may be the same rail twice or a left one and a right one, and those
// are different manuals. A reconstruction is made from ONE photograph, so a mirror pair comes
// out of this app as two copies of whichever unit was photographed — correct for half the
// furniture and inside-out for the other half.
//
// 'none' is not "we did not look": it is the answer where the part is not supplied in an even
// quantity, so there is no pair for the question to be about. Null IS "nobody said" — every
// part read before the field existed, and an even quantity the model left blank. The two are
// kept apart because only one of them is worth re-reading a manual over.
export const SYMMETRIES = ['none', 'identical', 'left-right'] as const;
export type Symmetry = (typeof SYMMETRIES)[number];

// WHICH OF A MIRROR PAIR A GESTURE IS HOLDING. Phase 4's answer, on the part entry rather
// than on the part: the part is both, and which one this sub-step fits is a fact about the
// sub-step. Null everywhere the question does not arise, which is nearly everywhere.
export type Side = 'left' | 'right';

// THE WORD FOR A SIDE, IN ONE PLACE. Not an i18n key, and that is the whole reason it is
// here: the sub-step sentences are written in BOTH languages at once (see assemblySlots,
// which renders textEn and textFr from one answer), so a translator taken from the page
// would put whichever language the reader is browsing in into both of them. Everything that
// spells a side — the sentence, the chips, the table, the viewer's toggle — reads this, so
// the bracket says the same word wherever it is drawn.
const SIDE_WORDS: Record<Side, { en: string; fr: string }> = {
  left: { en: 'Left', fr: 'Gauche' },
  right: { en: 'Right', fr: 'Droite' },
};

export const sideLabel = (side: Side, language: string): string =>
  (language === 'fr' ? SIDE_WORDS[side].fr : SIDE_WORDS[side].en);

// ONE OF A MIRROR PAIR, BY NAME: "Left side rail", "Traverse latérale gauche" — the side
// where each language puts an adjective.
export const sidedName = (name: string, side: Side | null, language: string): string => {
  if (!side) return name;
  const word = sideLabel(side, language);
  return language === 'fr'
    ? `${name} ${word.toLowerCase()}`
    : `${word} ${name.charAt(0).toLowerCase()}${name.slice(1)}`;
};

export type PartItem = {
  page: number;
  // The CURRENT box (what the table crops from): the refined one once the second
  // pass has run, else the bulk box.
  bbox: [number, number, number, number];
  // The bulk extraction's box (the first inference), kept even after the second pass
  // overwrites `bbox`, so the two can be compared on the PDF. Absent on parts stored
  // before this was tracked.
  firstBox?: [number, number, number, number];
  // Which of these a manual prints varies part to part; absent ones are null.
  letter: string | null; // assembly index (A, B, C…) reused in the steps
  // null when no count is printed (e.g. an overview thumbnail of all parts).
  qty: number | null;
  ref: string | null; // reference / article number
  name: string | null; // printed name/label, without the size
  // What the model says the part IS, in both languages — the one thing here it works
  // out instead of reading. Most manuals label the screws and nothing else: the four
  // biggest pieces of the chair arrive with no name at all, and a part with no name is
  // a part that cannot be searched for, catalogued, or spoken about in a tutorial.
  nameEn: string | null;
  nameFr: string | null;
  // What the part physically is, in one English sentence: shape, material, finish, holes.
  // NO SIZE. It used to carry an approximate one, invented from the proportions of a chair
  // the model had never measured; `dims` below carries a real one now, read off the
  // retailer's drawing, and a guess sitting in the same sentence as things you can see
  // would be indistinguishable from them.
  description: string | null;
  // The catalog item this part already IS, decided by the model — which is shown the
  // catalog and asked to match against it — or null when the database has never seen
  // this part. A matched part is named with the catalog's OWN name, so one part keeps
  // one name across every manual it turns up in.
  catalogId: number | null;
  size: PartSize | null;
  bag: string | null;
  // A tool used to assemble but not built into the furniture (allen key, etc.), so
  // it is reused across steps and never consumed.
  tool: boolean;
  // WHETHER IT IS IN THE BOX. True for everything drawn in the supplied-parts section,
  // which is nearly everything here — and false for the one case that section cannot
  // describe: a tool a STEP calls for and the carton does not contain. A manual that says
  // "tighten with a screwdriver" and never lists one leaves somebody finding that out at
  // the moment they need it, and a parts list that only ever held the box's contents had
  // nowhere to say so.
  //
  // Absent means supplied, and the migration is the same sentence: every part read before
  // this field existed came off the supplied page.
  supplied?: boolean;
  // Predicted ontology category for this part (or "OTHER"). Null if unclassified.
  category: string | null;
  // ONE OBJECT REPEATED, OR A MIRROR PAIR — see Symmetry. Absent on every part read before
  // the field was asked for, which is not 'none' and not 'identical': read symmetryOf, which
  // is the one place that decides what silence means.
  symmetry?: Symmetry | null;
};

// WHAT THIS PART'S SYMMETRY IS: what somebody answered, or what the count already implies.
//
// A STORED ANSWER ALWAYS STANDS, and that is the half worth being careful about. The same
// rule — a part not supplied in an even quantity has no pair to be a mirror of — is enforced
// against the MODEL where its reply lands (see the server's _part_symmetry, which will not let
// it contradict the qty it just gave). It is not enforced here, because the other thing that
// writes this field is a person clicking the cell, and a guard aimed at a model that answers
// eleven questions at once has no business overruling somebody who is looking at the drawing.
// A manual whose printed quantity is wrong is exactly the case where the hand has to win.
//
// So the arithmetic runs only on SILENCE, which is where it was always earning its keep: a
// manual read before the field existed has quantities and no symmetry, and this turns most of
// that list into real answers without a re-run.
//
// AND HARDWARE IS NEVER A PAIR, whatever it is supplied in. Four screws are four of one screw
// — a fastener has no handedness to have, so an even count of them raises no question rather
// than leaving one open. It is the other half of the rule the parts prompt states, and it is
// applied here for the same reason the qty half is: on silence, so a list read before the
// field existed comes out answered instead of a column of dashes down every screw.
//
// NOT ON THE SERVER, deliberately, and that is a gap with a name. _part_symmetry guards the
// model against contradicting the qty it was just given, because a qty is in the same reply;
// which GROUP a part is in is the ontology's answer and is not, so a coercion would have to
// reach for the categories table to say it. A model that calls a screw a mirror pair is left
// saying so, and the cell is where somebody disagrees — which is what the cell is for.
//
// Null only where the question is live and unanswered: a component, an even quantity, and
// nobody has classified it.
export const symmetryOf = (p: PartItem | undefined | null): Symmetry | null => {
  if (p?.symmetry) return p.symmetry;
  if (!p) return 'none';
  if (isHardwareOrTool(p)) return 'none';
  const qty = p.qty;
  if (typeof qty !== 'number' || qty < 2 || qty % 2) return 'none';
  return null;
};

// The one test the mesh viewer and the sub-step sentences make: is this part a pair with a
// handedness, so that a side has to be said about it?
export const isLeftRight = (p: PartItem | undefined | null): boolean =>
  symmetryOf(p) === 'left-right';

// A part's identity for deduplication: its reference, or failing that its
// assembly letter (manuals use one or the other).
const partId = (p: PartItem) => p.ref || p.letter;

// The key used to search a real image of a part and to select it: its reference
// when the manual prints one, else its name — printed, or failing that the model's.
// (A letter alone isn't searchable.)
export const partSearchKey = (p: PartItem) => p.ref || p.name || p.nameEn;

// The three kinds of thing a parts list holds, in the order it holds them: the furniture
// itself, what fastens it together, and what you need on the table to do it.
//
// The same three the Blender add-on groups its panel by, and named here so the app and the
// add-on cannot drift about which is which. Mapped FROM the catalog's categories rather
// than named by them, because those are edited by hand — "Hardware" became "Fasteners",
// "Washers" was split off it — and a category is per product ("Chair") where these three
// are what the sections of any parts list are.
export const PART_GROUPS = ['Components', 'Hardware', 'Tools'] as const;
export type PartGroup = (typeof PART_GROUPS)[number];

const GROUP_OF_KIND: Record<CategoryKind, PartGroup> = {
  component: 'Components',
  hardware: 'Hardware',
  tool: 'Tools',
};

// Which of the three a category belongs to: whatever the ONTOLOGY says it is.
//
// This was a map of category names, and it went stale every time somebody added a folder.
// "Other" was not a word it knew, so five pieces of hardware filed there sorted in with
// the seat and the rails; Washers had to be added by hand, then Barrel nuts, in four files
// each time. The kind is the fact that map was approximating, and it INHERITS — a folder
// added under Hardware tomorrow is hardware without anything here being told.
//
// `categories` defaults to the module cache, so the callers that cannot hold a
// subscription (a sort comparator, a plain predicate) still get the real answer.
//
// `seen` guards a cycle. Nothing should ever make a category its own ancestor, and a page
// that hung because somebody did would be a poor way to find out.
export function groupOfCategory(
  category: string | null | undefined,
  categories: CategoryDef[] = getCategories(),
): PartGroup {
  for (const cat of categoryChain(category, categories)) {
    if (cat.kind) return GROUP_OF_KIND[cat.kind];
  }
  // The same default the server keeps: a category nobody has classified is most likely a
  // product's own parts, and that is the branch that asks for a measurement rather than
  // skipping one.
  return 'Components';
}

// A category and every category above it, nearest first: ["barrel nuts", "hardware"].
//
// The walk groupOfCategory was doing inline, lifted out the day a second question needed
// it — "does this part belong to family X" rather than "what kind is it". Answering that
// by name alone would miss every child somebody adds later, which is the whole reason the
// ontology has parents.
export function categoryChain(
  category: string | null | undefined,
  categories: CategoryDef[] = getCategories(),
): CategoryDef[] {
  const chain: CategoryDef[] = [];
  let name = (category ?? '').trim().toLowerCase();
  // `seen` guards a cycle. Nothing should ever make a category its own ancestor, and a
  // page that hung because somebody did would be a poor way to find out.
  const seen = new Set<string>();
  while (name && !seen.has(name)) {
    seen.add(name);
    const cat = categories.find((c) => c.name.trim().toLowerCase() === name);
    if (!cat) break;
    chain.push(cat);
    name = (cat.parent || '').trim().toLowerCase();
  }
  return chain;
}

// Is this part in the given family, or in anything under it? Names are the ontology's own,
// lower-cased.
export const inCategoryFamily = (
  category: string | null | undefined,
  families: ReadonlySet<string>,
  categories: CategoryDef[] = getCategories(),
): boolean =>
  categoryChain(category, categories).some((c) => families.has(c.name.trim().toLowerCase()));

// A standalone piece of hardware or a tool: a screw, a dowel, a felt pad, an allen key.
// What they have in common is that they are the SAME OBJECT whatever the furniture is
// made of, so nothing about the product they belong to describes them.
// `categories` defaults to the module cache, which is [] until the ontology's one fetch
// lands — and [] resolves EVERY category to the default, component. Silence is a fine answer
// for a sort order; it is the wrong one for a caller deciding which parts go into a prompt,
// where it labels every screw a part of the furniture. Such a caller passes the list it
// waited for (see ensureCategories) rather than reading the cache and hoping.
// A part the list no longer has (re-listed since the steps were read) is neither: false.
export const isHardwareOrTool = (p: PartItem | undefined, categories: CategoryDef[] = getCategories()) =>
  !!p && (!!p.tool || groupOfCategory(p.category, categories) !== 'Components');

// The key a part's generated photos are stored under. Submodel-specific for real parts (so
// each colour keeps its own render), shared for hardware/tools. The \x1f separator can't
// occur in a part name/ref, so a submodel key never collides with the shared base key.
// Hardware/tools — or a notice with no submodel chosen at all — use the plain base key.
//
// THIS HAD A SECOND, NARROWER TEST of its own, matching the category names "hardware" and
// "tools" literally, and a comment arguing that widening it to isHardwareOrTool would send
// parts looking for photographs under a key they were never written to. The argument was
// sound and the ontology falsified it: Hardware split into Fasteners, Washers, Barrel nuts
// and Other, none of which is either word, so the narrow test stopped matching and every
// piece of hardware started asking for a submodel-scoped key. The photographs are under
// the PLAIN one — that is what the table holds — so the widening restores the old lookup
// rather than breaking it. Six parts on one notice read as ungenerated, each one a paid
// re-run away from being silently duplicated under the new key.
export const partGenKey = (p: PartItem, submodel: string): string => {
  const base = partSearchKey(p) || '';
  if (!base) return '';
  return isHardwareOrTool(p) || !submodel ? base : `${base}\x1f${submodel}`;
};

// The keys to LOOK a part's photographs up under, most specific first.
//
// Because the key above now depends on the ontology, and the ontology arrives over the
// network. A part whose key is computed before that fetch lands resolves to Components,
// asks for the submodel-scoped key, and misses — a race that would show as a photograph
// flickering in, or not arriving at all where the value is memoized.
//
// So a read tries the scoped key and falls back to the plain one. Safe by construction:
// \x1f cannot occur in a part's name or reference, so stripping it and everything after
// can only ever produce the shared key this same function would have chosen.
export const partGenKeys = (key: string): string[] => {
  const plain = key.split('\x1f')[0];
  return plain === key ? [key] : [key, plain];
};

// How many of the manual's pages ride along as context, and how wide they render.
//
// Small on purpose: these are read for what a part BECOMES — a chair, a shelf — not
// for their fine print, and every one of them is billed on every parts call. The
// backend caps this at the same number; the cap is here too so the bytes are never
// rendered, let alone posted, only to be dropped on arrival.
const CONTEXT_PAGES = 24;
const CONTEXT_WIDTH = 700;

// Collapse the false duplicates that come from a part appearing on more than one
// "Matériel fourni" section (an overview thumbnail without counts, plus the
// detailed list with quantities and reference/letter labels):
//   - drop overview entries: no quantity AND no identity,
//   - dedup by identity, keeping the entry that carries a quantity.
function dedupeParts(items: PartItem[]): PartItem[] {
  const real = items.filter((p) => p.qty != null || partId(p));
  const keptById = new Map<string, PartItem>();
  const out: PartItem[] = [];
  for (const p of real) {
    const id = partId(p);
    if (!id) {
      out.push(p);
      continue;
    }
    const existing = keptById.get(id);
    if (!existing) {
      keptById.set(id, p);
      out.push(p);
    } else if (existing.qty == null && p.qty != null) {
      out[out.indexOf(existing)] = p; // prefer the appearance that has a count
      keptById.set(id, p);
    }
  }
  return out;
}

// TWO PARTS MAY NOT SHARE A NAME.
//
// The other half of dedupeParts, and the harder half: that one collapses two rows that are
// the same part, this one separates two parts that are not. A manual can supply a
// screwdriver and call for a second it does not supply, and the read — correctly, since it
// is quoting what the page says — names both of them "Screwdriver". The table then has two
// columns headed the same word, the selector two identical tabs, and every question anyone
// asks about "the screwdriver" is a question about neither of them.
//
// A name that does not tell one thing from another is not a name, so the app is not allowed
// to hand out the same one twice. The manual almost always prints what separates them and
// the model is asked for it (see the parts prompt); this is what happens when it does not.
//
// SUFFIXED WITH A FACT THE PART ALREADY HAS, chosen per group of clashing names — the
// letter the manual labels it with, its reference, its printed designation — and only when
// that fact separates the WHOLE group. Numbering is the last resort, because "(1)" and
// "(2)" say only that there are two, which is the one thing the duplicate already said.
//
// The printed name (`name`) is never touched: it is the manual's own text, and the manual
// is allowed to print the same word twice.
const APART: ((p: PartItem) => string | null)[] = [
  (p) => p.letter,
  (p) => p.ref,
  (p) => p.size?.spec ?? null,
];

function nameApart(items: PartItem[]): PartItem[] {
  const groups = new Map<string, number[]>();
  items.forEach((p, i) => {
    const key = (p.nameEn || p.nameFr || '').trim().toLowerCase();
    if (!key) return;
    groups.set(key, [...(groups.get(key) ?? []), i]);
  });
  const out = [...items];
  for (const idx of groups.values()) {
    if (idx.length < 2) continue;
    const by = APART.find((f) => {
      const vs = idx.map((i) => (f(items[i]) || '').trim());
      return vs.every(Boolean) && new Set(vs).size === idx.length;
    });
    idx.forEach((i, n) => {
      const mark = by ? (by(items[i]) || '').trim() : String(n + 1);
      const p = items[i];
      out[i] = {
        ...p,
        nameEn: p.nameEn ? `${p.nameEn} (${mark})` : p.nameEn,
        nameFr: p.nameFr ? `${p.nameFr} (${mark})` : p.nameFr,
      };
    });
  }
  return out;
}

type Snapshot = {
  busy: boolean;
  items: PartItem[];
  error: string | null;
  // The model's reasoning per page (shown in the PAGE row's tooltip).
  reasoning: Record<number, string>;
  // The model's raw reply per page, shown beneath the reasoning in that tooltip — the
  // fallback for when the reasoning explains nothing, like the Phase 1/1 badges.
  raw: Record<number, string>;
  // Pages done / total while extracting (null when idle), shown like Phase 1.
  progress: { done: number; total: number } | null;
  // WHICH part is being re-read on its own, as its index in `items` — null when nothing
  // is, or when what is running is the whole phase.
  //
  // An index and not a flag, because the selector still works while the call is out: with
  // a flag, opening another part would show that part's rows waiting for an answer about
  // a different one. The list does not move under a scoped re-read (that is the whole
  // point of it), so the index it started with is still the part it is about.
  // Which part is being re-read on its own, or ALL_REREAD while a whole-list extraction has
  // not come back with anything yet. Null when nothing is out.
  rereading: number | typeof ALL_REREAD | null;
  // WHAT THE LAST BOX SEARCH SAID, in the order its calls were made — one entry per call.
  // Both forms write it: the one-part search over the whole manual, and the many-parts pass
  // over one annotated page, which is also what the extraction runs by itself at the end.
  //
  // It was thrown away. These two were the only calls in the phase whose badge could not
  // offer "Reasoning & output", so a box that landed somewhere strange was a number with
  // nothing behind it — and the automatic pass MOVES boxes, where why it moved one is the
  // whole difference between a correction and a shuffle.
  redo: RedoNote[];
  // WHICH DRAWINGS ARE BEING FOUND AGAIN: one part's index, ALL_REREAD for every part, or null.
  // In the store and not in the table, because the press is beside the phase button now (see
  // SchemaBoxesInfo) and the table is where the drawings it replaces dim.
  reboxing: number | typeof ALL_REREAD | null;
};
const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: {
    busy: false, items: loadItems(docId), error: null,
    reasoning: loadReason(docId), raw: loadRaw(docId), progress: null, rereading: null,
    redo: readState<RedoNote[]>(docId, redoKey(docId), []), reboxing: null,
  },
}));

const partsKey = (docId: string) => `notice.parts.${docId}`;
const reasonKey = (docId: string) => `notice.partsReason.${docId}`;
const redoKey = (docId: string) => `notice.partsRedo.${docId}`;
const rawKey = (docId: string) => `notice.partsRaw.${docId}`;

function loadItems(docId: string): PartItem[] {
  const items = readState<PartItem[]>(docId, partsKey(docId), []);
  if (!Array.isArray(items)) return [];
  // Where a notice stored before the size merge is brought up to date. It holds `size` as
  // a bare string — the manual's printed spec — and everything downstream now reads an
  // object, so without this the spec is silently invisible: `.spec` of a string is
  // undefined, the cell renders empty, and the row hides itself for having no values.
  return items.map((p) => ({ ...p, size: coerceSize(p.size) }));
}

const saveItems = (docId: string, items: PartItem[]) => writeState(docId, partsKey(docId), items);

function loadReason(docId: string): Record<number, string> {
  const o = readState<Record<number, string>>(docId, reasonKey(docId), {});
  return o && typeof o === 'object' ? o : {};
}

const saveReason = (docId: string, reasoning: Record<number, string>) =>
  writeState(docId, reasonKey(docId), reasoning);

function loadRaw(docId: string): Record<number, string> {
  const o = readState<Record<number, string>>(docId, rawKey(docId), {});
  return o && typeof o === 'object' ? o : {};
}

const saveRaw = (docId: string, raw: Record<number, string>) => writeState(docId, rawKey(docId), raw);
// Kept like the extraction's own two halves: what you want when a box looks wrong is what the
// call that placed it said, and that is a question asked long after the run.
const saveRedo = (docId: string, redo: RedoNote[]) => {
  writeState(docId, redoKey(docId), redo);
  update(docId, { redo });
};

// Merge a partial into this document's snapshot and re-render — the shared helper.
const update = store.patch;

// Every page of the manual, rendered small, as the context a parts call is given.
//
// Skipped pages (a render that fails) are simply absent: this is context, and a
// missing page costs the model a little understanding, not the call. Returns [] if the
// run is halted mid-render, which the caller's own halt check then acts on.
async function manualContext(
  doc: PDFDocumentProxy,
  halted: () => boolean,
): Promise<{ page: number; image: string }[]> {
  const out: { page: number; image: string }[] = [];
  const last = Math.min(doc.numPages, CONTEXT_PAGES);
  if (doc.numPages > CONTEXT_PAGES) {
    // Said out loud: a manual longer than this is read for its parts but understood
    // only from its first pages, and a cap nobody mentions reads as full coverage.
    // eslint-disable-next-line no-console
    console.info(
      `[notice] parts context: pages 1-${CONTEXT_PAGES} of ${doc.numPages} (the rest are not sent)`,
    );
  }
  for (let n = 1; n <= last; n++) {
    if (halted()) return [];
    const image = await renderPdfPageToImage(doc, n, CONTEXT_WIDTH);
    if (image) out.push({ page: n, image });
  }
  return out;
}

// What the parts route answers for one page.
type PartsReply = {
  parts: {
    bbox: [number, number, number, number];
    letter: string | null;
    qty: number | null;
    ref: string | null;
    name: string | null;
    nameEn?: string | null;
    nameFr?: string | null;
    description?: string | null;
    catalogId?: number | null;
    // The backend answers the merged shape; a string is what OLD stored parts hold.
    size: PartSize | string | null;
    bag: string | null;
    tool?: boolean;
    // Absent from every reply before the field existed, and absent means supplied — the
    // same reading the server's normaliser takes. See PartItem.supplied.
    supplied?: boolean;
    // The page the box is on, when it is not the page being read — see toItem.
    page?: number | null;
    category?: string | null;
    // Absent from every reply before the field existed, and null where the model left an
    // even quantity unclassified — see the server's _part_symmetry, which is where the two
    // are told apart. Neither is 'none'.
    symmetry?: Symmetry | null;
  }[];
  reasoning?: string;
  raw?: string;
};

// ONE parts page, read.
//
// Full page with the off-band rows masked white — NOT a cropped strip. The model reads
// box_2d against the whole page (so the box is page-relative, no band transform), and the
// true page aspect ratio keeps every box accurate; a cropped strip skewed them all by one
// uniform offset.
//
// No abort signal: once sent, this call is billed whatever happens, so its answer is kept.
// Which is why `halted` is checked between the render and the post rather than around
// them: rendering is local and free, and the last moment a halt can still save money is
// after it and before the call goes out.
async function readPartsPage(
  doc: PDFDocumentProxy,
  page: number,
  band: { top: number; bottom: number },
  context: { page: number; image: string }[],
  halted: () => boolean,
): Promise<PartsReply | null> {
  const image = await renderPdfPageMasked(doc, page, band.top, band.bottom);
  if (!image || halted()) return null;
  const { data } = await axios.post<PartsReply>(
    '/api/notice/parts', { model: RUN_MODEL, image, page, context },
  );
  return data;
}

// One line of that reply as a part. box_2d was read against the full (masked) page, so it
// is already page-relative — no band offset to add.
//
// EXCEPT WHERE THE MODEL NAMED ANOTHER PAGE, which one kind of entry does: a tool the steps
// call for and the carton does not hold is not on the page being read at all — it is drawn in
// the step that uses it — so it is boxed against that context page and says which. `page` is
// the page this call read, and is what everything else keeps.
const toItem = (page: number, p: PartsReply['parts'][number]): PartItem => ({
  page: p.page ?? page,
  bbox: p.bbox,
  firstBox: p.bbox,
  letter: p.letter ?? null,
  qty: p.qty ?? null,
  ref: p.ref ?? null,
  name: p.name ?? null,
  nameEn: p.nameEn ?? null,
  nameFr: p.nameFr ?? null,
  description: p.description ?? null,
  catalogId: p.catalogId ?? null,
  size: coerceSize(p.size),
  bag: p.bag ?? null,
  tool: p.tool ?? false,
  // Carried through UNCHANGED, undefined and all: undefined is what "supplied" looks
  // like, and writing `?? true` here would file a fact the reply did not state.
  supplied: p.supplied,
  category: p.category ?? null,
  // ONE OBJECT REPEATED, OR A MIRROR PAIR. Carried through as given, undefined and all, for
  // the reason `supplied` above is: the server distinguishes "not a pair" from "nobody
  // classified this pair", and a default written in here would collapse the two.
  symmetry: p.symmetry,
});

// The size a part carries out of a re-read: the half that was just read, over the half
// that was measured.
//
// Two calls fill one object. The parts read has the manual's printed designation in front
// of it and nothing else; the axes were measured off a retailer's dimension drawing by a
// call that is not running. So a re-read keeps every measured axis, and takes the printed
// fields it actually answered — a field it said nothing about this time keeps what it said
// last time rather than being cleared by a silence.
function mergedSize(prior: PartSize | null, fresh: PartSize | null): PartSize | null {
  if (!prior) return fresh;
  const answered = Object.fromEntries(
    Object.entries(printedSize(fresh)).filter(([, v]) => v != null),
  );
  const merged = {
    ...drawnSize(prior),
    ...printedSize(prior),
    ...answered,
    source: prior.source, note: prior.note,
  } as PartSize;
  return hasSize(merged) ? merged : null;
}

// Extract the parts list from the category's pages (in `bands`), one Gemini call
// per page. The page band is rendered and posted; each returned box (relative to
// the band) is converted to page-relative coordinates and accumulated. Results
// stream in page by page, replacing any previous extraction.
// `resume` keeps what a halted run already extracted and asks only for the pages it
// never reached. A page's presence in `reasoning` is what marks it done — every page
// that answered records one, empty string included.
//
// `only` narrows the whole thing to ONE part: see reReadOnePart.
export async function extractParts(
  docId: string,
  file: Blob,
  bands: Bands,
  t: (k: string) => string,
  resume = false,
  only?: PartItem | null,
) {
  const entry = store.getEntry(docId);
  if (entry.snapshot.busy) return;
  if (!bands.model || bands.pages.length === 0) {
    update(docId, { error: t('notice.cat2.noRun') });
    return;
  }
  if (only) return reReadOnePart(docId, file, bands, t, only);

  const halted = haltGate(docId);
  // The sizes read off the listing's dimension drawing, kept across a re-run.
  //
  // They do NOT come out of the manual — a manual gives a bolt "M6 x 70mm" and a seat
  // nothing — so a fresh extraction answers `dims: null` for every part, and re-running
  // this phase would silently throw away a dimension read that cost a model call. Keyed
  // by the same identity the dedupe uses, so a part that survives the re-run keeps its
  // size and one the manual no longer lists simply does not come back.
  const priorDims = new Map<string, PartSize>();
  for (const p of entry.snapshot.items) {
    const id = partId(p);
    if (id && p.size && (p.size.x != null || p.size.y != null || p.size.z != null)) {
      priorDims.set(id, p.size);
    }
  }
  const priorItems = resume ? entry.snapshot.items : [];
  const priorReason = resume ? entry.snapshot.reasoning : {};
  const priorRaw = resume ? entry.snapshot.raw : {};
  // WHAT STAYS ON SCREEN WHILE THE FIRST PAGE IS OUT: the list this document already had,
  // marked as being re-read.
  //
  // The store used to be emptied here, on the sound argument that the previous run's answer
  // must never be read as this one's. The cost was the whole phase going blank — no selector,
  // no table, no rows — for the length of a call, which on a one-page manual is the whole of
  // it: a minute of empty page under a spinning button, with nothing to say what is coming.
  //
  // So the shape stays and every cell that the extraction writes says it is waiting. See
  // ALL_REREAD, and PartsTable, which already had the per-cell wait for the one-part re-read.
  // The moment the first page answers, `items` becomes this run's and the mark is dropped.
  const carried = resume ? priorItems : entry.snapshot.items;
  update(docId, {
    busy: true,
    error: null,
    items: carried,
    rereading: resume ? null : ALL_REREAD,
    reasoning: priorReason,
    raw: priorRaw,
    progress: { done: Object.keys(priorReason).length, total: bands.pages.length },
  });
  if (!resume) {
    saveItems(docId, []);
    saveReason(docId, {});
    saveRaw(docId, {});
  }

  let loadingTask: PDFDocumentLoadingTask | null = null;
  try {
    const buf = await file.arrayBuffer();
    loadingTask = loadPdf(buf);
    const doc: PDFDocumentProxy = await loadingTask.promise;
    const items: PartItem[] = [...priorItems];
    const reasoning: Record<number, string> = { ...priorReason };
    const raw: Record<number, string> = { ...priorRaw };
    let done = Object.keys(priorReason).length;
    const total = bands.pages.length;

    // The whole manual, once, before the loop: it is the same context for every parts
    // page, and rendering it per call would redraw the same pages two or three times.
    // Rendering is local and free — a halt during it costs nothing.
    const context = await manualContext(doc, halted);

    for (const page of bands.pages) {
      if (halted()) break;
      // Already answered, in the run being resumed: its parts are in `items` and its
      // call is paid for. Asking again buys the same list twice.
      if (page in reasoning) continue;
      const band = bands.bands[page] ?? { top: 0, bottom: 1 };
      const data = await readPartsPage(doc, page, band, context, halted);
      if (halted()) break;
      if (!data) {
        update(docId, { progress: { done: ++done, total } });
        continue;
      }

      reasoning[page] = data.reasoning || '';
      raw[page] = data.raw || '';
      for (const p of data.parts || []) items.push(toItem(page, p));
      // Show the deduplicated list (false duplicates collapsed across pages), with any
      // size this notice had already read put back on the parts it belongs to.
      // The freshly-read spec, plus the axes the dimension call had already measured.
      // The two halves come from different calls and only one of them just ran.
      const shown = nameApart(dedupeParts(items).map((p) => {
        const id = partId(p);
        const kept = id ? priorDims.get(id) : undefined;
        return kept ? { ...p, size: mergedSize(kept, p.size) } : p;
      }));
      saveItems(docId, shown);
      saveReason(docId, { ...reasoning });
      saveRaw(docId, { ...raw });
      update(docId, {
        // THIS RUN'S LIST, from here on: the carried one is gone and nothing on screen is the
        // previous answer any more, so the mark comes off with it.
        items: shown, rereading: null, reasoning: { ...reasoning }, raw: { ...raw },
        progress: { done: ++done, total },
      });
    }
    // The bulk pass now has every part's identity. Its weak spot is the BOX of the small
    // parts (hardware / tools): drawing 12 fields for 13 parts in one reply starves the
    // tiny drawings of the attention their placement needs. Tighten just those boxes the
    // way the "Refaire" button does — one described part, full page, box-only — where the
    // model is reliably accurate. Structural parts box well in bulk and are left alone.
    if (!halted()) await refineSmallPartBoxes(doc, docId, RUN_MODEL, halted);
    // A WHOLE LIST'S French names go to the database it matched (see setCatalogNamesFr); a
    // halted one has not read every page.
    if (!halted()) void setCatalogNamesFr(store.getEntry(docId).snapshot.items);
  } catch (e) {
    // NAMED BY WHAT FAILED, not by the status line. See callError, which turns a bare
    // gateway 502 into something to do about it.
    update(docId, {
      error: t('notice.parts.listFailed').replace('{why}', callError(e, t)),
    });
  } finally {
    // The mark comes off whatever happened. A run that was halted or failed before its first
    // page leaves the carried list on screen, and a table still claiming to be waiting for a
    // call that is not coming is the one state worse than a stale one.
    update(docId, { busy: false, progress: null, rereading: null });
    void loadingTask?.destroy();
  }
}

// How much two boxes are the same box, 0 to 1. Format-agnostic (the boxes are
// [ymin, xmin, ymax, xmax] in whatever scale they were read at, and only their ratio is
// used), so nothing here has to know whether that is 0-1 or 0-1000.
function boxIoU(
  a: [number, number, number, number], b: [number, number, number, number],
): number {
  const top = Math.max(a[0], b[0]);
  const left = Math.max(a[1], b[1]);
  const bottom = Math.min(a[2], b[2]);
  const right = Math.min(a[3], b[3]);
  if (bottom <= top || right <= left) return 0;
  const overlap = (bottom - top) * (right - left);
  const area = (box: [number, number, number, number]) =>
    Math.max(0, box[2] - box[0]) * Math.max(0, box[3] - box[1]);
  const union = area(a) + area(b) - overlap;
  return union > 0 ? overlap / union : 0;
}

// Which of a page's freshly read parts is the one that was asked about.
//
// Three tries, weakest last. A reference or a letter is the manual's own identifier and
// settles it. A name settles it nearly as well, and has to allow the model's three names
// to have swapped places between two runs. And failing both — an unlettered part with no
// printed name, which is most large pieces — the DRAWING is still in the same place on the
// page, so the box says which part this is even when nothing written down does.
//
// Returning null is a real answer: better to say the part was not found again than to
// overwrite a barrel nut with a wood dowel because it was the only thing left.
function matchPart(fresh: PartItem[], target: PartItem): PartItem | null {
  const id = partId(target);
  const byId = id ? fresh.find((p) => partId(p) === id) : undefined;
  if (byId) return byId;

  const wanted = new Set(
    [target.name, target.nameEn, target.nameFr]
      .map((n) => (n || '').trim().toLowerCase()).filter(Boolean),
  );
  const byName = wanted.size
    ? fresh.find((p) => [p.name, p.nameEn, p.nameFr]
      .some((n) => n && wanted.has(n.trim().toLowerCase())))
    : undefined;
  if (byName) return byName;

  let best: PartItem | null = null;
  let bestIoU = 0;
  for (const p of fresh) {
    const iou = boxIoU(p.bbox, target.bbox);
    if (iou > bestIoU) { bestIoU = iou; best = p; }
  }
  // A third of the box in common. Two drawings in a parts strip do not overlap, so
  // anything above this is the same drawing read twice with a slightly different edge.
  return bestIoU >= 0.3 ? best : null;
}

// RE-READ ONE PART, and leave every other part exactly as it is.
//
// The extraction is per PAGE — one call reads the whole strip — so this costs the same
// call the full run would for that page. What it saves is not money but the LIST: a full
// re-run rewrites all thirteen parts, and a name, a description or a category somebody has
// since corrected goes back to whatever the model says this time. When the reason to re-run
// is one part (a field the prompt has learned about since, a size read wrong), rewriting
// the other twelve is a cost with no benefit attached.
//
// So the page is read and exactly one row is replaced.
//
// TWO THINGS ARE KEPT off the old row. The BOX, because a re-read is not what it is for
// and the bulk pass boxes a small part worse than the per-part refine already did — the
// schema has its own Redo, right there in the row. And the measured axes, because they
// came from the dimension read and this call has nothing to say about them.
//
// The sizes are NOT read afterwards, unlike the full run. That call answers by index into
// the whole list it is sent, so there is no such thing as running it for one part; it
// belongs to the press that means the whole phase.
async function reReadOnePart(
  docId: string,
  file: Blob,
  bands: Bands,
  t: (k: string) => string,
  target: PartItem,
) {
  const halted = haltGate(docId);
  const at = store.getEntry(docId).snapshot.items.indexOf(target);
  update(docId, {
    busy: true, error: null, progress: { done: 0, total: 1 },
    rereading: at >= 0 ? at : null,
  });

  let loadingTask: PDFDocumentLoadingTask | null = null;
  try {
    const buf = await file.arrayBuffer();
    loadingTask = loadPdf(buf);
    const doc: PDFDocumentProxy = await loadingTask.promise;
    const context = await manualContext(doc, halted);
    if (halted()) return;

    const band = bands.bands[target.page] ?? { top: 0, bottom: 1 };
    const data = await readPartsPage(doc, target.page, band, context, halted);
    if (halted() || !data) return;

    const match = matchPart((data.parts || []).map((p) => toItem(target.page, p)), target);
    if (!match) {
      update(docId, { error: t('notice.parts.oneNotFound') });
      return;
    }

    const current = store.getEntry(docId).snapshot.items;
    const index = current.indexOf(target);
    if (index < 0) return; // the list changed under us; drop this result
    // Through the same guard as the bulk read: this part's name is fresh off the model and
    // the twelve beside it are not, so a re-read is exactly where a name can walk back into
    // a collision. Idempotent — a name that was already told apart is unique, so it forms no
    // group and is never marked twice.
    const items = nameApart(current.map((p, i) => (i === index ? {
      ...match,
      bbox: p.bbox,
      firstBox: p.firstBox,
      size: mergedSize(p.size, match.size),
    } : p)));
    update(docId, { items, progress: { done: 1, total: 1 } });
    saveItems(docId, items);
  } catch (e) {
    update(docId, {
      error: t('notice.parts.oneFailed').replace('{why}', callError(e, t)),
    });
  } finally {
    update(docId, { busy: false, progress: null, rereading: null });
    void loadingTask?.destroy();
  }
}

// HOW WIDE A PAGE GOES UP WHEN THE WHOLE MANUAL DOES, and how many. One page alone went at
// 1600, which is the width the table crops from; twenty-four of those is a payload nobody
// should post to find one screwdriver. 1100 is what the manual-wide info read used, and a
// box is normalized, so the width costs precision rather than meaning.
const BOX_PAGES = 24;
const BOX_WIDTH = 1100;

// Every page of the manual, rendered and numbered, as the search a single Redo is given.
// A page that will not render is simply absent — the model names pages by the caption it is
// given, and one missing page costs a place to look, not the call.
async function boxPages(doc: PDFDocumentProxy): Promise<{ page: number; image: string }[]> {
  const out: { page: number; image: string }[] = [];
  for (let n = 1; n <= Math.min(doc.numPages, BOX_PAGES); n++) {
    const image = await renderPdfPageToImage(doc, n, BOX_WIDTH);
    if (image) out.push({ page: n, image });
  }
  return out;
}

// What one part's description is on the wire, for the two calls that go looking for its
// drawing. One shape, so the badge's preview and the press cannot describe it differently.
const boxAsk = (item: PartItem) => ({
  letter: item.letter, ref: item.ref, name: item.name,
  nameEn: item.nameEn, nameFr: item.nameFr, size: item.size, qty: item.qty,
});

// Ask the model where ONE part is drawn, over EVERY page of the manual: the page and the box
// on it, both page-relative, so they drop straight into the part. Null when it can't be read.
//
// The whole manual, and it used to be the part's own page. A tool the steps call for and the
// carton does not hold is not on the supplied-parts page at all — it is drawn in the step that
// uses it — so the old call could only ever answer with somebody else's drawing, and the one
// button for fixing a box was the one that could not fix that box.
//
// A reply with no page is a reply about the page it was asked about, which is the page the
// part already claims: the server drops any page it did not send, so `page` here is either a
// page of this manual or nothing.
async function requestPartBox(
  pages: { page: number; image: string }[],
  item: PartItem,
  model: string,
): Promise<{
  page: number | null;
  bbox: [number, number, number, number];
  note: { reasoning: string; raw: string };
} | null> {
  const { data } = await axios.post<{
    bbox: [number, number, number, number]; page?: number | null;
    reasoning?: string; raw?: string;
  }>('/api/notice/part-box', { model, pages, part: boxAsk(item) });
  const bbox = data.bbox;
  if (!Array.isArray(bbox) || bbox.length !== 4) return null;
  return {
    page: data.page ?? null,
    bbox,
    note: { reasoning: data.reasoning || '', raw: data.raw || '' },
  };
}

// Patch one part's box off the CURRENT snapshot (found by identity, so a concurrent
// extraction / relocation that moved things drops this result), and persist it.
function patchPartBox(
  docId: string,
  item: PartItem,
  bbox: [number, number, number, number],
  page?: number | null,
) {
  const current = store.getEntry(docId).snapshot.items;
  const index = current.indexOf(item);
  if (index < 0) return; // the list changed under us; drop this result
  // THE PAGE MOVES WITH THE BOX or nothing does. A box is a rectangle on a page and the two
  // are one answer: written apart, a screwdriver found in step 3 would carry step 3's
  // rectangle over the supplied-parts page and crop a piece of the parts list.
  const items = current.map((p, i) => (
    i === index ? { ...p, bbox, ...(page != null ? { page } : {}) } : p
  ));
  update(docId, { items });
  saveItems(docId, items);
}

// An empty size: every field a size has, all of them unanswered. What a patch lands on
// when nothing has been read for a part yet — and what a size built from somewhere other
// than the dimension read starts from, so no caller has to enumerate fifteen nulls.
export const NO_SIZE: PartSize = {
  x: null, y: null, z: null, xBottom: null, xTop: null, yBottom: null, yTop: null,
  spec: null, thread: null, lengthMm: null,
  bodyDiaMm: null, headDiaMm: null, fitsHoleMm: null, driveSizeMm: null, shortLegMm: null,
  drive: null, forScrew: null, tip: null, source: null, note: null,
};

// Write one MEASURING call's answer onto a part's size, by POSITION in the list.
//
// By position because that is what these calls answer with: they are sent the list and
// reply about index 4, and index 4 is the part they looked at. Matching on names instead
// would go wrong on the two things a manual both calls "Vis", and on anything the model
// tidied up on its way through.
//
// A PATCH and not a size. The measuring calls do not all answer the same rows: the
// dimension read gives three axes, and the estimate gives whichever rows the part's
// category has and the readings left empty — a barrel nut's length and body diameter,
// where a rail's are x, y and z. So the keys present in the patch are written, and every
// other field of the size stays exactly as it was. A spec transcribed off the manual by
// the parts read survives a measurement, which is the property this has always had and
// the reason it merges at all.
//
// A key present and null is an ERASURE and must stay one. A dimension read that correctly
// finds nothing answers three nulls, and it has to be able to clear a number a previous
// read invented — so this cannot skip nulls, only keys.
// IN THE BOX, OR NOT. By hand: the read answers it for every part it lists, and the
// judgement belongs to whoever has the manual open — a screwdriver drawn in a step is the
// model's guess at what the carton holds, and the person unpacking it knows.
// RENAMED BY HAND, in the language being read.
//
// Because a manual can hold two of a thing the model has one word for. This one lists a
// screwdriver among what the carton holds and calls for another in step 3 that it does not
// supply — and the read, correctly, named both of them "Screwdriver". Two identical columns
// is not the read being wrong, it is the read being as specific as the page was; the person
// with the box open knows the supplied one is the little PVC-handled thing.
//
// ONE LANGUAGE'S FIELD, the one on screen, because that is exactly what partName reads and
// what the reader was looking at when they disagreed with it. Writing both would put an
// English phrase in the French name of every part somebody corrects in English.
//
// A blank clears it back to the printed name, which is the only undo this needs.
// PARTS NO MANUAL LISTED, added at the end: a folder's, made from the objects of an imported
// model (see ProductImport). Named in both languages alike, with no page and no box to crop.
export function addParts(docId: string, parts: { name: string; catalogId: number }[]): void {
  const added: PartItem[] = parts.map(({ name, catalogId }) => ({
    page: 0, bbox: [0, 0, 0, 0], letter: null, qty: 1, ref: null, name, nameEn: name, nameFr: name,
    description: null, catalogId, size: null, bag: null, tool: false, category: null,
  }));
  const items = [...store.getEntry(docId).snapshot.items, ...added];
  update(docId, { items });
  saveItems(docId, items);
}

export function setPartName(docId: string, index: number, language: 'fr' | 'en', name: string): void {
  const current = store.getEntry(docId).snapshot.items;
  if (index < 0 || index >= current.length) return;
  const clean = name.trim() || null;
  const items = current.map((p, i) => (
    i === index ? { ...p, ...(language === 'fr' ? { nameFr: clean } : { nameEn: clean }) } : p
  ));
  update(docId, { items });
  saveItems(docId, items);
}

export function setPartSupplied(docId: string, index: number, supplied: boolean): void {
  const current = store.getEntry(docId).snapshot.items;
  if (index < 0 || index >= current.length) return;
  const items = current.map((p, i) => (i === index ? { ...p, supplied } : p));
  update(docId, { items });
  saveItems(docId, items);
}

// ONE OBJECT REPEATED, OR A MIRROR PAIR — said by hand, from the table's own cell.
//
// The one field here a person can settle better than the run can. Whether two rails are
// mirror images is a fact about the drawing that a reader with the manual open sees at a
// glance and a model reads out of a small grey picture, and there is no list to choose from
// and no second call to re-run: the answer is one of three words and somebody knows it.
//
// Written like every other correction in this table — into the store and straight to disk, no
// call — and it wins over the count, which is what symmetryOf is careful about.
export function setPartSymmetry(docId: string, index: number, symmetry: Symmetry): void {
  const current = store.getEntry(docId).snapshot.items;
  if (index < 0 || index >= current.length) return;
  const items = current.map((p, i) => (i === index ? { ...p, symmetry } : p));
  update(docId, { items });
  saveItems(docId, items);
}

export function setPartSize(
  docId: string,
  index: number,
  patch: Partial<PartSize> & { source: 'measured' | 'derived' | null; note: string | null },
  // A WHOLE NEW ANSWER, not a correction to the old one. The size read is a fresh look at
  // what the manual prints, so what it does not say this time the part does not have — and
  // merging kept values from readings taken when the part was filed somewhere else.
  //
  // That is not a hypothetical: a wood button estimated as a bounding box under the
  // catch-all category left x/y/z of 12 by 12 by 6 behind. Refiled under Fastener covers it
  // is described by a diameter and a hole, the table stopped showing those axes — and the
  // 3D viewer went on labelling the mesh "wanted 12 mm" from numbers nothing on screen
  // admitted to holding.
  //
  // The estimate keeps merging, and that is the difference between them: it answers ONE
  // field it was asked for and has nothing to say about the rest.
  { replace = false }: { replace?: boolean } = {},
) {
  const current = store.getEntry(docId).snapshot.items;
  if (index < 0 || index >= current.length) return; // the list changed under us
  const items = current.map((p, i) => {
    if (i !== index) return p;
    // Nothing measured and nothing printed is no size at all, stored as null rather than
    // as an object of nulls. The two would read the same in the table and differently
    // everywhere else — `size ? ... : ...` is the test a dozen callers make.
    const merged: PartSize = replace
      ? { ...NO_SIZE, ...patch }
      : { ...NO_SIZE, ...(p.size ?? {}), ...patch };
    return { ...p, size: hasSize(merged) ? merged : null };
  });
  update(docId, { items });
  saveItems(docId, items);
}

// Ask the model to correct several small parts at once against an ANNOTATED page
// (every part's current box drawn + tagged). Returns the corrected boxes keyed by tag;
// a tag the model leaves out keeps its bulk box.
async function requestRefinedBoxes(
  image: string,
  parts: { tag: string; item: PartItem }[],
  model: string,
): Promise<{
  boxes: Record<string, [number, number, number, number]>;
  note: { reasoning: string; raw: string };
}> {
  const { data } = await axios.post<{
    boxes: { tag: string; bbox: [number, number, number, number] }[];
    reasoning?: string; raw?: string;
  }>(
    '/api/notice/refine-boxes',
    {
      model,
      image,
      parts: parts.map(({ tag, item }) => ({
        tag,
        letter: item.letter, ref: item.ref, name: item.name,
        nameEn: item.nameEn, nameFr: item.nameFr, size: item.size, qty: item.qty,
      })),
    },
  );
  const out: Record<string, [number, number, number, number]> = {};
  for (const b of data.boxes || []) {
    if (Array.isArray(b.bbox) && b.bbox.length === 4) out[b.tag] = b.bbox;
  }
  return { boxes: out, note: { reasoning: data.reasoning || '', raw: data.raw || '' } };
}

// After a bulk extraction, re-box the small parts (hardware/tools) in ONE call per
// page: render the page with EVERY part's current box drawn and tagged, and ask the
// model to correct the small ones (drawn red). Showing all boxes at once lets it pull
// a misplaced box onto a drawing no other part has taken — the cross-part context a
// per-part redo on a blank page can't have. Only the small parts are patched; the
// confident big-part boxes stay put. Best-effort: a page whose call fails keeps its
// bulk boxes. Usually one page; a small pool covers the rare multi-page section.
async function refineSmallPartBoxes(
  doc: PDFDocumentProxy,
  docId: string,
  model: string,
  halted: () => boolean,
): Promise<void> {
  const items = store.getEntry(docId).snapshot.items;
  // THE ONES WITH A BOX TO MOVE, whoever supplied them. This used to exclude everything
  // unsupplied, on the argument that a tool the carton does not hold has no drawing to
  // correct — which was true only because the extraction was told not to look for one. It is
  // now told to box those tools on the step page that draws them, so an unsupplied tool has a
  // page and a rectangle like anything else, and excluding it left the one box in the manual
  // found by a different reading as the one box never checked.
  //
  // The guard that remains is the real one: a DEGENERATE box is nothing to move, and a model
  // handed an annotated page and asked to correct a rectangle that is not there will answer
  // with one somewhere. That is a fact about the box, not about the carton.
  const onPage = (p: PartItem) => hasBox(p.bbox);
  const pages = [...new Set(items.filter((p) => isHardwareOrTool(p) && onPage(p)).map((p) => p.page))];
  if (!pages.length) return;
  // One entry per page asked, in the order the answers land. This pass is the one nobody
  // presses — it runs at the end of every extraction — so it is also the one whose answer
  // there was no way at all to read.
  const notes: RedoNote[] = [];

  const runPage = async (page: number) => {
    if (halted()) return;
    // Every part on this page, tagged 1..N; the small ones are the ones to fix.
    const tagged = items
      .filter((p) => p.page === page && onPage(p))
      .map((item, i) => ({ tag: String(i + 1), item, fix: isHardwareOrTool(item) }));
    const image = await renderPdfPageAnnotated(
      doc,
      page,
      tagged.map((t) => ({ bbox: t.item.bbox, tag: t.tag, fix: t.fix })),
    );
    if (!image || halted()) return;
    try {
      const { boxes: fixes, note } = await requestRefinedBoxes(
        image, tagged.filter((t) => t.fix), model,
      );
      notes.push({ page, part: null, ...note });
      for (const t of tagged) {
        const bbox = fixes[t.tag];
        if (t.fix && bbox) patchPartBox(docId, t.item, bbox);
      }
    } catch {
      // leave this page's bulk boxes in place
    }
  };

  let cursor = 0;
  const worker = async () => {
    while (cursor < pages.length) {
      if (halted()) return;
      await runPage(pages[cursor++]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(4, pages.length) }, () => worker()));
  // By page, whatever order the four workers finished in: the panel reads as the manual does.
  saveRedo(docId, notes.sort((a, b) => (a.page ?? 0) - (b.page ?? 0)));
}

// Re-locate ONE part's drawing and replace its box, when the first extraction put
// the box on empty page (a blank white crop). Renders that part's whole page, asks
// the model for just this part's box (identified by whatever the manual printed for
// it plus its worked-out name), and patches the one item — persisted, so the fix
// survives a reload. Everything else about the part is left exactly as it was. This is
// the "Refaire" button; the same core auto-runs on the small parts after extraction.
export async function relocatePart(
  docId: string,
  file: Blob,
  index: number,
  model: string,
  t: (k: string) => string,
): Promise<void> {
  const item = store.getEntry(docId).snapshot.items[index];
  if (!item) return;

  const buf = await file.arrayBuffer();
  const task = loadPdf(buf);
  try {
    const doc = await task.promise;
    const pages = await boxPages(doc);
    if (!pages.length) throw new Error(t('notice.err.generic'));
    const found = await requestPartBox(pages, item, model);
    if (!found) throw new Error(t('notice.err.generic'));
    saveRedo(docId, [{ page: found.page, part: index, ...found.note }]);
    patchPartBox(docId, item, found.bbox, found.page);
  } finally {
    void task.destroy();
  }
}

// "Redo all" — re-box EVERY part in ONE call per page, the same batch mechanism the
// small-part refine uses, but with every part marked to fix. The page is rendered FULL
// with all current boxes drawn + tagged, and the model returns the whole corrected set at
// once — far faster than one full-page call per part, and it keeps the cross-part context
// (no two boxes on one drawing). Boxes come back page-relative. One PDF load; a small pool
// covers the rare multi-page section; every result applied in ONE write at the end, off the
// current snapshot (identities that survived a concurrent edit).
export async function relocateAllParts(docId: string, file: Blob, model: string): Promise<void> {
  const items = store.getEntry(docId).snapshot.items;
  if (!items.length) return;
  const buf = await file.arrayBuffer();
  const task = loadPdf(buf);
  try {
    const doc = await task.promise;
    const pages = [...new Set(items.filter((p) => hasBox(p.bbox)).map((p) => p.page))];
    const found = new Map<PartItem, [number, number, number, number]>();
    // What each page's call said — see refineSmallPartBoxes, which collects the same thing
    // for the pass that runs by itself.
    const notes: RedoNote[] = [];
    const runPage = async (page: number) => {
      // Every part whose box is on this page, tagged 1..N; ALL are the ones to correct
      // (fix = red). Unsupplied tools included — they now carry the page they are drawn on,
      // so a screwdriver boxed in step 3 is corrected on step 3's page with anything else
      // there.
      //
      // NOT THE ONES WITH NOTHING TO FIND: a part whose box is degenerate has no rectangle to
      // correct, and a model asked to correct one that is not there answers with a rectangle
      // somewhere. Its own Redo reads the whole manual and is the way to give it a first box
      // — see relocatePart.
      const tagged = store
        .getEntry(docId)
        .snapshot.items.filter((p) => p.page === page && hasBox(p.bbox))
        .map((item, i) => ({ tag: String(i + 1), item }));
      if (!tagged.length) return;
      const image = await renderPdfPageAnnotated(
        doc,
        page,
        tagged.map((t) => ({ bbox: t.item.bbox, tag: t.tag, fix: true })),
      );
      if (!image) return;
      try {
        const { boxes: fixes, note } = await requestRefinedBoxes(image, tagged, model);
        notes.push({ page, part: null, ...note });
        for (const t of tagged) {
          const bbox = fixes[t.tag];
          if (bbox) found.set(t.item, bbox);
        }
      } catch {
        // leave this page's boxes; the per-part Refaire can retry any of them
      }
    };
    // STOP SENDS NO NEXT PAGE (see runHalt): the ones out land and are kept, and a page it never
    // reached keeps the note it had.
    const halted = haltGate(docId);
    const ran = new Set<number>();
    let cursor = 0;
    const worker = async () => {
      while (cursor < pages.length && !halted()) {
        const page = pages[cursor++];
        ran.add(page);
        await runPage(page);
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, pages.length) }, () => worker()));
    const kept = store.get(docId).redo.filter((n) => n.part == null && n.page != null && !ran.has(n.page));
    saveRedo(docId, [...notes, ...kept].sort((a, b) => (a.page ?? 0) - (b.page ?? 0)));
    if (!found.size) return;
    const current = store.getEntry(docId).snapshot.items;
    const patched = current.map((p) => {
      const bbox = found.get(p);
      return bbox ? { ...p, bbox } : p;
    });
    update(docId, { items: patched });
    saveItems(docId, patched);
  } finally {
    void task.destroy();
  }
}

// FIND THE DRAWINGS AGAIN, for one part or every part — the full-page detection, which is the
// reliable pass (the bulk extraction reads a cropped band, and that is what skews a box onto
// empty page). On the CURRENT model: redoing a box is new work.
//
// A failure leaves the boxes as they are; the press can be made again.
export async function redoPartBoxes(
  docId: string, file: Blob, only: number | undefined, t: (k: string) => string,
): Promise<void> {
  if (store.get(docId).reboxing !== null) return;
  update(docId, { reboxing: only ?? ALL_REREAD });
  try {
    if (only == null) await relocateAllParts(docId, file, RUN_MODEL);
    else await relocatePart(docId, file, only, RUN_MODEL, t);
  } catch {
    // see above
  } finally {
    update(docId, { reboxing: null });
  }
}

export const usePartsRun = store.useSnapshot;

// WHAT THE EXTRACTION WOULD ASK — the prompt half of the badge beside the phase's run button.
//
// From the server, because most of this prompt is the categories a part may be and the parts
// other manuals have already named, and neither exists here. A preview assembled on the client
// would be a different prompt from the one that gets sent. No image goes with it: the pages are
// rendered per call, and what is being previewed is the words.
export async function partsPrompt(): Promise<{ prompt: string; model: string }> {
  const { data } = await axios.post<{ prompt?: string }>(
    '/api/notice/parts', { model: RUN_MODEL, preview: true },
  );
  return { prompt: data.prompt || '', model: RUN_MODEL };
}

// THE PICTURES THE CALL CARRIES, for the panel that says what the button would send.
//
// The prompt was the only half of it on screen, and a parts call is mostly pictures: the
// supplied-parts page masked to its section, and the manual's first pages behind it. Reading
// the words alone told you nothing about the half of the question that is images — which
// page was masked, how much of it survived the mask, how many pages rode along.
//
// Rendered HERE, by the same two functions readPartsPage and manualContext call, at the same
// widths and from the same PDF. Not an illustration of the call: the same pixels.
//
// Two groups, drawn in the order the call sends them — the context, then the section it
// reads, last and nearest the prompt. Kept as two functions because they are two questions
// about the call; PartsListInfo runs both and concatenates. See the parts route, which builds
// its image list exactly this way.
// The pages this phase actually READS: one call each, each one masked to the band Phase 1
// filed as supplied parts. The mask is the point — it is what the model is shown, and a
// section that turns out to be half a page of something else is visible here and nowhere
// else before the call is paid for.
export async function partsReadPictures(
  file: Blob,
  bands: Bands,
  t: (k: string) => string,
): Promise<{ url: string; label: string; caption: string }[]> {
  return withDoc(file, async (doc) => {
    const out = [];
    for (const page of bands.pages) {
      const band = bands.bands[page] ?? { top: 0, bottom: 1 };
      const image = await renderPdfPageMasked(doc, page, band.top, band.bottom);
      if (image) {
        // TWO LINES, and the first one is what the CALL calls this image: it goes up
        // labelled "THE SUPPLIED-PARTS SECTION" with no page number at all. Captioned by its
        // page alone it was the second tile in the strip reading "PAGE 1" — the same words as
        // the context render of the same page, for a picture that is masked and is read and
        // is last. The page it was cut from is the second line, because that is the question
        // the first line raises.
        out.push(asPicture(
          image,
          t('notice.parts.promptPageRead').replace('{n}', String(page)),
          `${t('notice.parts.promptSectionTag')}\n${t('notice.pdf.page')} ${page}`,
        ));
      }
    }
    return out;
  }, []);
}

// And the whole manual behind them, small — what a part BECOMES. Capped at CONTEXT_PAGES,
// which is why this is drawn rather than described: a manual of forty pages sends twenty-four
// of them, and the panel is the only place anybody can count.
export async function partsContextPictures(
  file: Blob,
  t: (k: string) => string,
): Promise<{ url: string; label: string; caption: string }[]> {
  return withDoc(file, async (doc) => {
    const out = [];
    for (let n = 1; n <= Math.min(doc.numPages, CONTEXT_PAGES); n++) {
      const image = await renderPdfPageToImage(doc, n, CONTEXT_WIDTH);
      if (image) {
        out.push(asPicture(
          image,
          t('notice.parts.promptPageContext').replace('{n}', String(n)),
          `${t('notice.pdf.page')} ${n}`,
        ));
      }
    }
    return out;
  }, []);
}

// WHAT THE REDO BUTTON WOULD ASK, in the form the current scope would ask it.
//
// TWO PROMPTS BEHIND ONE BUTTON, because there are two calls: one part open is one full page
// and one box back; "All parts" is the page with every current box drawn and tagged and the
// whole corrected set back. They read differently and a badge that showed the wrong one would
// be describing a call the press will not make — so the caller passes what it is scoped to and
// gets the question that goes with it.
//
// The picture is not previewed. It is the manual's own page, rendered by the browser for the
// call, and for the many-parts form it does not exist until the boxes are drawn on it — a
// render per hover to show a page already on screen two rows up.
export async function partBoxPrompt(item: PartItem): Promise<{ prompt: string; model: string }> {
  const { data } = await axios.post<{ prompt?: string }>('/api/notice/part-box', {
    preview: true, model: RUN_MODEL, part: boxAsk(item),
  });
  return { prompt: data.prompt || '', model: RUN_MODEL };
}

// The many-parts form: every part on ONE page, tagged the way the annotated render tags them.
// The first page holding any, since that is the first call the press makes — the pages after
// it ask the same question about their own parts.
// WHICH PAGE THE MANY-PARTS FORM WOULD ASK ABOUT, and every part on it, tagged as the
// annotated render tags them. One function because the prompt and the picture MUST agree:
// the prompt refers to its parts by a number drawn on the page, so a preview that numbered
// them from a different list would be a legend for another picture.
function firstPageTags(items: PartItem[]) {
  const onPage = items.filter((p) => hasBox(p.bbox));
  const page = onPage[0]?.page;
  const parts = onPage.filter((p) => p.page === page);
  return { page, tagged: parts.map((item, i) => ({ tag: String(i + 1), item })) };
}

export async function refineBoxesPrompt(
  items: PartItem[],
): Promise<{ prompt: string; model: string }> {
  const tagged = firstPageTags(items).tagged.map(({ tag, item }) => ({
    tag,
    letter: item.letter, ref: item.ref, name: item.name,
    nameEn: item.nameEn, nameFr: item.nameFr, size: item.size, qty: item.qty,
  }));
  if (!tagged.length) return { prompt: '', model: RUN_MODEL };
  const { data } = await axios.post<{ prompt?: string }>('/api/notice/refine-boxes', {
    preview: true, model: RUN_MODEL, parts: tagged,
  });
  return { prompt: data.prompt || '', model: RUN_MODEL };
}

// AND THE PAGE IT WOULD BE READ AGAINST. The prompt says "normalized to 0-1000 against THIS
// image" and then, until now, showed no image at all — which is a preview contradicting the
// only sentence in it that matters.
//
// The two forms carry different pictures, and the difference IS the difference between the
// calls: one part goes up as the page whole, every part goes up as the page with each current
// box drawn on it and numbered, which is what the tags in that prompt are numbering. Rendered
// here from the same file and at the same width the press uses.
//
// Its own loader (see usePromptCall's `more`): a 1600px page render is not something to make
// on the way to showing a paragraph.
export async function redoPictures(
  file: Blob,
  items: PartItem[],
  only: number | undefined,
  t: (k: string) => string,
): Promise<{ url: string; label: string; caption: string }[]> {
  const one = only != null ? items[only] : undefined;
  return withDoc(file, async (doc) => {
    if (one) {
      // EVERY PAGE, because that is what this press sends: the part may be drawn anywhere,
      // and a preview showing the one page it currently claims would be showing the search
      // that used to be made rather than the one that will be.
      return (await boxPages(doc)).map(({ page, image }) => asPicture(
        image,
        t('notice.parts.redoPicOne').replace('{n}', String(page)),
        `${t('notice.pdf.page')} ${page}`,
      ));
    }
    const { page, tagged } = firstPageTags(items);
    if (page == null || !tagged.length) return [];
    const image = await renderPdfPageAnnotated(
      doc,
      page,
      tagged.map(({ tag, item }) => ({ bbox: item.bbox, tag, fix: true })),
    );
    return image ? [asPicture(
      image,
      t('notice.parts.redoPicAll').replace('{n}', String(page)),
      `${t('notice.pdf.page')} ${page}`,
    )] : [];
  }, []);
}

// The extracted parts, outside React — for the orchestrator, which hands them to the
// repartition immediately after this phase's own call has filled them in.
export const getParts = (docId: string): PartItem[] => store.get(docId).items;

// The extracted parts as boxes to overlay on the manual's pages, each labelled
// with the part's name in the read language. Shared by the main reader (Phase 1)
// and Phase 2's category preview so both draw the same boxes over the PDF.
export function usePartBoxes(docId: string): PartBox[] {
  const { items } = usePartsRun(docId);
  const { language } = useLanguage();
  return useMemo(
    () =>
      items.map((p) => ({
        page: p.page,
        bbox: p.bbox,
        firstBox: p.firstBox,
        label: (language === 'fr' ? p.nameFr : p.nameEn) || p.name || p.nameEn || p.nameFr || '',
      })),
    [items, language],
  );
}
